import { EditableLable, MoreDropdown } from '@/components';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import { useLanguages } from '@/features/translations/hooks/useTranslations';
import { cn } from '@/lib';
import { formatDate } from '@/utils/dateUtils';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DocumentFile } from '../../types';
import { downloadDocumentFile, openDocumentFileToView } from '../../utils/documentFileActions';
import { getCategoryLabel } from '../../utils/getCategoryLabel';
import { getFileTypeIcon } from '../../utils/getFileTypeIcon';
import type { DragItemData } from '../document-explorer/useDragAndDrop';
import { FloatingActionBar } from '../FloatingActionBar';
import { DocumentDownloadButtonsCompact } from '../partials/DocumentDownloadButtonsCompact';
import { EmptyColumn } from '../partials/EmptyColumn';
import {
  DeviceModelSummary,
  DocumentListProps,
  DocumentTableHeaderProps,
  FileAction,
  FileActionsProps,
  FileRowProps,
  FolderAction,
  FolderActionsProps,
  FolderRowProps,
} from './DocumentList.types';

const HEADER_COLUMN_CLASS =
  'text-onsurface/60 px-4 py-2.5 text-center text-xs font-semibold tracking-wide uppercase';
const CONTAINER_CLASS =
  '[&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full grow overflow-hidden flex flex-col';


//const ROW_DIVIDER_STYLE = { boxShadow: '0 1px 0 0 var(--color-border)' };

// Renders the document table and coordinates folder/file row interactions.
export function DocumentList({
  folders,
  files,
  permission,
  deviceModelNames,
  onFolderClick,
  onFileClick,
  onFolderAction,
  editingFolderId,
  editingFolderName,
  isRenamingFolder,
  onFolderRenameStart,
  onFolderRenameChange,
  onFolderRenameSubmit,
  onFolderRenameCancel,
  onFileAction,
  sentinelRef,
  isFetchingNextPage,
  isDndActive = false,
  draggedItem = null,
  isSelected,
  onToggleSelect,
  selectedCount = 0,
  isBulkMoving = false,
  isBulkDeleting = false,
  onBulkMoveClick,
  onBulkDeleteClick,
}: DocumentListProps) {
  const isAdmin = permission === 'admin';
  const renameInputRef = useRenameInputFocus(editingFolderId);
  const { data: languages = [] } = useLanguages();
  const languageNamesById = useMemo(
    () => new Map(languages.map((language) => [language.id, language.name])),
    [languages]
  );

  // When the dragged item is part of the selection, dim all selected items.
  // Otherwise only dim the single item being dragged.
  const isDraggingSelection = isDndActive && Boolean(draggedItem && isSelected?.(draggedItem.item.id));
  const shouldDimItem = (id: string) => {
    if (!isDndActive) return false;
    return isDraggingSelection ? (isSelected?.(id) ?? false) : draggedItem?.item.id === id;
  };

  return (
    <div className={cn(CONTAINER_CLASS, 'relative')}>
      <Table>
        <DocumentTableHeader isAdmin={isAdmin} />
        <TableBody>
          {folders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              isAdmin={isAdmin}
              isEditingFolder={editingFolderId === folder.id}
              editingFolderName={editingFolderName}
              isRenamingFolder={isRenamingFolder}
              renameInputRef={renameInputRef}
              onFolderClick={onFolderClick}
              onFolderAction={onFolderAction}
              onFolderRenameStart={onFolderRenameStart}
              onFolderRenameChange={onFolderRenameChange}
              onFolderRenameSubmit={onFolderRenameSubmit}
              onFolderRenameCancel={onFolderRenameCancel}
              isDndActive={isDndActive}
              isDraggedItem={shouldDimItem(folder.id)}
              isSelected={isSelected?.(folder.id) ?? false}
              onToggleSelect={isAdmin ? onToggleSelect : undefined}
            />
          ))}
          {files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              isAdmin={isAdmin}
              deviceModelNames={deviceModelNames}
              languageNamesById={languageNamesById}
              onFileClick={onFileClick}
              onFileAction={onFileAction}
              isDndActive={isDndActive}
              isDraggedItem={shouldDimItem(file.id)}
              isSelected={isSelected?.(file.id) ?? false}
              onToggleSelect={isAdmin ? onToggleSelect : undefined}
            />
          ))}
        </TableBody>
      </Table>
      <div ref={sentinelRef} className="h-6 w-full" aria-hidden="true" />
      {isFetchingNextPage ? (
        <div className="flex items-center justify-center py-4">
          <div className="border-primary size-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : null}

      {isAdmin ? (
        <FloatingActionBar
          selectedCount={selectedCount}
          isBulkMoving={isBulkMoving}
          isBulkDeleting={isBulkDeleting}
          onMoveClick={onBulkMoveClick ?? (() => { })}
          onDeleteClick={onBulkDeleteClick ?? (() => { })}
        />
      ) : null}
    </div>
  );
}

// Renders sticky table headers with the admin-specific actions column.
function DocumentTableHeader({ isAdmin }: DocumentTableHeaderProps) {
  const { t } = useTranslation();

  return (
    <TableHeader>
      {isAdmin ? <th className={cn(HEADER_COLUMN_CLASS)} aria-label="Select" /> : null}
      <th className={cn(HEADER_COLUMN_CLASS, '')}>
        <span className="sr-only">{t('documents.table.columns.icon')}</span>
      </th>
      <th className={cn(HEADER_COLUMN_CLASS, "text-left")}>{t('documents.table.columns.name')}</th>
      <th className={cn(HEADER_COLUMN_CLASS, 'w-[100px]')}>{t('common.language')}</th>
      <th className={HEADER_COLUMN_CLASS}>{t('documents.table.columns.type')}</th>
      <th className={HEADER_COLUMN_CLASS}>{t('documents.table.columns.category')}</th>
      <th className={HEADER_COLUMN_CLASS}>{t('documents.table.columns.updated')}</th>
      <th className={cn(HEADER_COLUMN_CLASS, "text-right")}>{t('documents.table.columns.actions')}</th>
    </TableHeader>
  );
}

// Renders a folder row. The entire row is draggable for admin users.
function FolderRow({
  folder,
  isAdmin,
  isEditingFolder,
  editingFolderName,
  isRenamingFolder,
  renameInputRef,
  onFolderClick,
  onFolderAction,
  onFolderRenameStart,
  onFolderRenameChange,
  onFolderRenameSubmit,
  onFolderRenameCancel,
  isDndActive = false,
  isDraggedItem = false,
  isSelected = false,
  onToggleSelect,
}: FolderRowProps) {
  const { t } = useTranslation();

  // Full-row drag — listeners spread directly on the <tr>
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: `folder:${folder.id}`,
    data: { type: 'folder', item: folder } satisfies DragItemData,
    disabled: !isAdmin,
  });

  // Droppable target — folder rows accept drops from other items
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: folder.id,
    disabled: !isAdmin,
  });

  // Combine drag + drop refs onto the same <tr> node
  const setRowRef = (el: HTMLTableRowElement | null) => {
    setDragRef(el);
    setDropRef(el);
  };

  const isDropHighlighted = isDndActive && isOver && !isDraggedItem;
  const isDimmed = isDndActive && isDraggedItem;

  const handleRowClick = (event: React.MouseEvent) => {
    if (isEditingFolder) return;
    // Cmd/Ctrl+click toggles selection instead of navigating
    if (isAdmin && onToggleSelect && (event.metaKey || event.ctrlKey)) {
      onToggleSelect(folder);
      return;
    }
    onFolderClick(folder);
  };

  return (
    <tr
      ref={setRowRef}
      {...(isAdmin ? attributes : {})}
      {...(isAdmin ? listeners : {})}
      className={cn(
        'cursor-pointer transition-colors',
        isDropHighlighted ? 'bg-primary/10 ring-2 ring-inset ring-primary' : 'hover:bg-surface-darker/50',
        isDimmed && 'opacity-40'
      )}
      onClick={handleRowClick}
    >
      <CheckboxColumn
        isAdmin={isAdmin}
        isSelected={isSelected}
        ariaLabel={t('documents.selectFolder', { name: folder.name })}
        onToggle={() => onToggleSelect?.(folder)}
      />

      <IconColumn iconType='folder' />
      <td
        className="text-onsurface px-4 py-3 text-sm font-medium text-left"
        onClick={(event) => { if (isEditingFolder) event.stopPropagation(); }}
        onDoubleClick={(event) => {
          if (!isAdmin) return;
          event.preventDefault();
          event.stopPropagation();
          onFolderRenameStart(folder);
        }}
      >
        <EditableLable
          ref={renameInputRef}
          isEditing={isEditingFolder}
          value={editingFolderName}
          setValue={onFolderRenameChange}
          onSet={() => onFolderRenameSubmit(folder)}
          onCancel={onFolderRenameCancel}
          maxLength={255}
          disabled={isRenamingFolder}
          aria-label={t('documents.folderForm.nameLabel')}
        >
          <span>{folder.name}</span>
        </EditableLable>
      </td>
      <EmptyColumn />
      <EmptyColumn />
      <EmptyColumn />
      <td className="text-onsurface/70 px-4 py-3 text-sm text-center">
        {formatDate(folder.createdAt)}
      </td>
      {isAdmin ? (
        <td
          className="px-4 py-3 flex items-center justify-end"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <FolderActions folder={folder} onAction={onFolderAction} />
        </td>
      ) : (
        <EmptyColumn />
      )}
    </tr>
  );
}



// Renders a file row. The entire row is draggable for admin users.
function FileRow({
  file,
  isAdmin,
  deviceModelNames,
  languageNamesById,
  onFileClick,
  onFileAction,
  isDndActive = false,
  isDraggedItem = false,
  isSelected = false,
  onToggleSelect,
}: FileRowProps) {
  const { t } = useTranslation();
  const deviceModels = getDeviceModelSummary(file, deviceModelNames);
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const defaultFileObject = file.fileObject ?? file.fileObjects?.[0];
  const fileLanguageLabel =
    defaultFileObject?.languageId != null
      ? languageNamesById.get(defaultFileObject.languageId)
      : undefined;

  const handleView = async () => {
    if (!defaultFileObject) return;
    setIsViewing(true);
    try {
      await openDocumentFileToView({ fileId: file.id, objectId: defaultFileObject.id });
    } catch {
      toast.error(t('documents.downloadDialog.viewError'));
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownload = async () => {
    if (!defaultFileObject) return;
    setIsDownloading(true);
    try {
      await downloadDocumentFile({ fileId: file.id, objectId: defaultFileObject.id });
    } catch {
      toast.error(t('documents.downloadDialog.downloadError'));
    } finally {
      setIsDownloading(false);
    }
  };

  // Full-row drag — listeners spread directly on the <tr>
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `file:${file.id}`,
    data: { type: 'file', item: file } satisfies DragItemData,
    disabled: !isAdmin,
  });

  const isDimmed = isDndActive && isDraggedItem;
  // File rows are not droppable — show a "no-drop" cursor when another item is being dragged
  const showNoDrop = isDndActive && !isDraggedItem;

  const handleRowClick = (event: React.MouseEvent) => {
    // Cmd/Ctrl+click toggles selection instead of opening panel
    if (isAdmin && onToggleSelect && (event.metaKey || event.ctrlKey)) {
      onToggleSelect(file);
      return;
    }
    onFileClick(file);
  };

  return (
    <tr
      ref={setNodeRef}
      {...(isAdmin ? attributes : {})}
      {...(isAdmin ? listeners : {})}
      className={cn(
        'cursor-pointer transition-colors',
        showNoDrop && 'cursor-no-drop',
        isDimmed ? 'opacity-40' : 'hover:bg-surface-darker/50'
      )}
      onClick={handleRowClick}
    >
      <CheckboxColumn
        isAdmin={isAdmin}
        isSelected={isSelected}
        ariaLabel={t('documents.table.selectAriaLabel', { name: file.name })}
        onToggle={() => onToggleSelect?.(file)}
      />

      <IconColumn iconType={file.fileType} />

      <td className="text-onsurface px-4 py-3 text-sm font-medium text-left">
        <div className="space-y-1">
          <span>{file.name}</span>
          {/* {fileLanguageLabel ? (
            <p className="text-onsurface/60 text-xs">{fileLanguageLabel}</p>
          ) : null} */}
          {file.uploadState?.status === 'uploading' ? (
            <p className="text-primary text-xs font-medium">
              {t('documents.table.uploading', { progress: file.uploadState.progress })}
            </p>
          ) : null}
          {file.uploadState?.status === 'failed' ? (
            <p className="text-red-600 text-xs font-medium">
              {t('documents.table.uploadFailedStatus')}
            </p>
          ) : null}
        </div>
      </td>

      <td className="text-onsurface/70 px-4 py-3 text-sm text-center">
        {fileLanguageLabel ? (
          <p className="text-onsurface/60 text-xs">{fileLanguageLabel}</p>
        ) : "-"}
      </td>

      <td className="text-onsurface/70 px-4 py-3 text-sm text-center" title={deviceModels.title}>
        {deviceModels.label || '-'}
      </td>
      <td className="text-onsurface/70 px-4 py-3 text-sm text-center">
        {getCategoryLabel(file.category, t)}
      </td>
      <td className="text-onsurface/70 px-4 py-3 text-sm text-center">
        {formatDate(file.updatedAt)}
      </td>
      <td
        className="px-4 py-3 w-[100px]"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <DocumentDownloadButtonsCompact
            isViewing={isViewing}
            isDownloading={isDownloading}
            isBusy={!defaultFileObject || isViewing || isDownloading}
            onView={handleView}
            onDownload={handleDownload}
            viewAriaLabel={t('documents.downloadDialog.view')}
            downloadAriaLabel={t('documents.downloadDialog.download')}
          />
          {isAdmin ? (
            <FileActions file={file} onAction={onFileAction} />
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// Renders available row actions for a folder.
function FolderActions({ folder, onAction }: FolderActionsProps) {
  const { t } = useTranslation();
  const actions: { key: FolderAction, label: string }[] = [
    { key: FolderAction.Rename, label: t('documents.rowActions.rename') },
    { key: FolderAction.Move, label: t('documents.rowActions.move') },
    { key: FolderAction.Delete, label: t('documents.rowActions.delete') },
  ];
  return <MoreDropdown
    label={t('documents.table.actionsAriaLabel', { name: folder.name })}
    actions={actions}
    onAction={(key) => onAction(key, folder)}
    width={180}
  />
}

// Renders available row actions for a file.
function FileActions({ file, onAction }: FileActionsProps) {
  const { t } = useTranslation();
  const actions: { key: FileAction, label: string }[] = [
    { key: FileAction.Move, label: t('documents.rowActions.move') },
    { key: FileAction.Delete, label: t('documents.rowActions.delete') },
  ];


  return <MoreDropdown
    label={t('documents.table.actionsAriaLabel', { name: file.name })}
    actions={actions}
    onAction={(key) => onAction(key, file)}
    width={180}
  />
}

// Focuses and selects the folder rename input whenever editing starts.
function useRenameInputFocus(editingFolderId: string | null) {
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editingFolderId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [editingFolderId]);

  return renameInputRef;
}

// Builds the compact device model label and optional tooltip for a file row.
function getDeviceModelSummary(
  file: DocumentFile,
  deviceModelNames: Map<number, string>
): DeviceModelSummary {
  const modelNames = file.deviceAccess
    .map((access) => deviceModelNames.get(access.deviceModelId))
    .filter((value): value is string => Boolean(value));

  if (modelNames.length === 0) return { label: '' };

  return {
    label: modelNames[0]!,
    title: modelNames.length > 1 ? modelNames.join(', ') : undefined,
  };
}


export function CheckboxColumn({
  isAdmin,
  isSelected,
  ariaLabel,
  onToggle
}: {
  isAdmin: boolean;
  isSelected?: boolean;
  ariaLabel: string;
  onToggle: () => void
}) {
  if (!isAdmin) return null;

  return (
    <td
      className="px-3 py-3 whitespace-nowrap justify-center content-center"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >

      <div className="flex justify-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="border-border size-4 rounded accent-primary cursor-pointer"
          aria-label={ariaLabel}
        />
      </div>
    </td>
  );
}


export function IconColumn({ iconType }: { iconType: string | null }) {

  const icon = getFileTypeIcon(iconType);
  const className = "text-primary size-4 shrink-0 mx-auto";

  return (
    <td className="whitespace-nowrap text-center">

      {createElement(icon, {
        className: className,
        'aria-hidden': 'true',
      })}
    </td>
  );
}

